import { useMemo } from 'react';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/hooks/useTheme';
import { useAuth } from '@/features/auth/hooks/useAuth';
import Logo from './Logo';
import { cn, darkenHexColor } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';

type FooterProps = {
  className?: string;
};

export function Footer({ className = '' }: FooterProps) {
  const { colors } = useTheme();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const year = useMemo(() => new Date().getFullYear(), []);

  const gradientFrom = darkenHexColor(colors.primary, 14);
  const gradientMid = colors.primary;
  const gradientTo = darkenHexColor(colors.primary, -10);

  return (
    <footer
      className={cn(
        'mt-auto border-t border-white/20 px-4 py-3 text-xs shadow-inner backdrop-blur-xl dark:border-white/5',
        className
      )}
      style={{
        background: `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientMid} 55%, ${gradientTo} 100%)`,
        color: colors.primaryText,
      }}
    >
      <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <a
            href="https://www.louisbelet.ch/en/"
            target="_blank"
            rel="noreferrer noopener"
            className="rounded-xl border border-white/20 bg-white/10 p-1.5 transition hover:bg-white/20"
          >
            <Logo className="h-6 w-auto" />
          </a>
          <span className="font-semibold opacity-75">
            © {year} {t('common.brand')}. {t('footer.rights')}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isAuthenticated && (
            <Link
              to="/contact"
              className="rounded-full border border-white/20 bg-white/10 px-3 py-1 font-semibold transition hover:bg-white/20"
              style={{ color: colors.primaryText }}
            >
              {t('footer.contactSupport')}
            </Link>
          )}
          <a
            href="https://www.louisbelet.ch/en/"
            target="_blank"
            rel="noreferrer noopener"
            className="flex items-center gap-1 rounded-full border border-white/20 bg-white/10 px-3 py-1 font-semibold transition hover:bg-white/20"
            style={{ color: colors.primaryText }}
          >
            {t('footer.exploreLink')} <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    </footer>
  );
}
